import { useEffect, useState } from 'react';
import { useScrollProgress, mapRange } from '../heroSignals';

export default function Headline() {
  const [mounted, setMounted] = useState(false);
  // Normalized pointer position (-1 ~ 1), then smoothed for parallax
  const [pointer, setPointer] = useState({ x: 0, y: 0 });
  const scroll = useScrollProgress();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    let rafId = 0;
    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };

    const handleMove = (e: PointerEvent) => {
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = (e.clientY / window.innerHeight) * 2 - 1;
      target.x = Math.max(-1, Math.min(1, nx));
      target.y = Math.max(-1, Math.min(1, ny));
    };
    const handleLeave = () => {
      target.x = 0;
      target.y = 0;
    };

    const tick = () => {
      current.x += (target.x - current.x) * 0.08;
      current.y += (target.y - current.y) * 0.08;
      setPointer({ x: current.x, y: current.y });
      rafId = requestAnimationFrame(tick);
    };
    tick();

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerleave', handleLeave);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerleave', handleLeave);
    };
  }, []);

  const baseWord =
    'inline-block will-change-transform ease-[cubic-bezier(0.22,1,0.36,1)]';

  // Stage 1 fade: scroll 0.10 -> 0.20 fully out
  const fadeT = mapRange(scroll, 0.10, 0.20);
  const opacity = 1 - fadeT;
  const blurPx = fadeT * 14;
  const dispersion = fadeT * 40; // words drift outward as they fade

  const wordStyle = (delay: number) => ({
    fontFamily:
      'Inter, "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif',
    fontWeight: 900,
    fontSize: '9vw',
    letterSpacing: '-0.02em',
    color: '#FFFFFF',
    transform: mounted ? 'translateY(0%)' : 'translateY(105%)',
    transition: 'transform 1100ms cubic-bezier(0.22,1,0.36,1)',
    transitionDelay: `${delay}ms`,
  });

  // Parallax offsets — opposite direction to the model creates a subtle drift.
  const precisionOffset = {
    x: -pointer.x * 14 + dispersion,
    y: -pointer.y * 6 - dispersion * 0.6,
  };
  const soundOffset = {
    x: -pointer.x * 8 + dispersion * 0.8,
    y: -pointer.y * 12 + dispersion,
  };
  const dividerOffset = {
    x: -pointer.x * 4,
    y: -pointer.y * 3,
  };
  const axiomOffset = {
    x: -dispersion * 0.9,
    y: 0,
  };

  const stageStyle: React.CSSProperties = {
    opacity,
    filter: blurPx > 0.01 ? `blur(${blurPx}px)` : undefined,
    willChange: 'opacity, filter, transform',
    transition: 'opacity 120ms linear, filter 120ms linear',
  };

  return (
    <div
      className="pointer-events-none absolute inset-0 z-10"
      style={stageStyle}
    >
      {/* Left word: AXIOM (vertically centered) */}
      <div
        className="absolute top-1/2 -translate-y-1/2 overflow-hidden will-change-transform"
        style={{
          left: '6vw',
          lineHeight: 0.9,
          padding: '0.05em 0.02em',
          transform: `translate3d(${axiomOffset.x}px, ${axiomOffset.y}px, 0) translateY(-50%)`,
          transition: 'transform 120ms linear',
        }}
      >
        <span className={baseWord} style={wordStyle(150)}>
          AXIOM
        </span>
      </div>

      {/* Right top: PRECISION */}
      <div
        className="absolute text-right overflow-hidden will-change-transform"
        style={{
          right: '6vw',
          top: '18vh',
          lineHeight: 0.9,
          padding: '0.05em 0.02em',
          transform: `translate3d(${precisionOffset.x}px, ${precisionOffset.y}px, 0)`,
          transition: 'transform 120ms linear',
        }}
      >
        <span className={baseWord} style={wordStyle(280)}>
          PRECISION
        </span>
      </div>

      {/* Divider between the two right-side words: serial + thin line */}
      <div
        className="absolute flex items-center gap-3 will-change-transform"
        style={{
          right: '6vw',
          top: '50%',
          transform: `translate3d(${dividerOffset.x}px, calc(-50% + ${dividerOffset.y}px), 0)`,
          transition: 'transform 120ms linear',
          fontFamily:
            'Inter, "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif',
        }}
      >
        <span
          className="tracking-[0.28em] uppercase"
          style={{
            color: 'rgba(255,255,255,0.55)',
            fontSize: '11px',
            fontWeight: 500,
          }}
        >
          N° 001 — 2026
        </span>
        <span
          className="block"
          style={{
            width: '9vw',
            height: '1px',
            background:
              'linear-gradient(to right, rgba(255,255,255,0.45), rgba(255,255,255,0))',
          }}
        />
      </div>

      {/* Right bottom: SOUND */}
      <div
        className="absolute text-right overflow-hidden will-change-transform"
        style={{
          right: '6vw',
          bottom: '20vh',
          lineHeight: 0.9,
          padding: '0.05em 0.02em',
          transform: `translate3d(${soundOffset.x}px, ${soundOffset.y}px, 0)`,
          transition: 'transform 120ms linear',
        }}
      >
        <span className={baseWord} style={wordStyle(380)}>
          SOUND
        </span>
      </div>
    </div>
  );
}